import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import ActivityTimeline from "@/hq/ActivityTimeline";
import { Users, Building2, TrendingUp, TrendingDown } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, Area, AreaChart, CartesianGrid,
} from "recharts";

const CUR_SYM = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const money = (m, c) => `${CUR_SYM[c] || c + " "}${(Number(m || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const totalByCur = (obj) => obj && Object.keys(obj).length ? Object.entries(obj).map(([c, v]) => money(v, c)).join(" · ") : "—";

const SERVICE_LABEL = {
  orange_optimise: "Orange Optimise",
  orange_optimise_pro: "Optimise Pro",
  orange_care: "OrangeCare",
  premium_website: "Website",
  crm_business_system: "CRM",
  landing_page: "Landing Page",
  orange_fix: "Orange Fix",
};

function useCountUp(value, duration = 800) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setV(value); return; }
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setV(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return v;
}

// Clickable primary KPI — wraps the card in a router Link so the operator
// can drill through immediately. Preserves the exact original visual card
// (padding, count-up value, icon, orange eyebrow).
function PrimaryKPI({ label, value, icon: Icon, testId, to, children }) {
  const shown = useCountUp(Number(value || 0));
  const body = (
    <Card className="p-6 md:p-8 hover:border-[#FF6A00] transition-colors" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">{label}</div>
        <Icon size={18} className="text-[#FF6A00]"/>
      </div>
      <div className="mt-4 font-black text-5xl md:text-6xl tracking-[-0.03em] tabular-nums">{shown}</div>
      {children}
    </Card>
  );
  return to ? <Link to={to} data-testid={`${testId}-link`}>{body}</Link> : body;
}

function MiniKPI({ label, value, tone = "slate", testId, to }) {
  const toneMap = { orange: "text-[#FF6A00]", green: "text-[#0FA958]", red: "text-[#C1350B]", slate: "text-[#0A0A0A]" };
  const body = (
    <Card className={`p-3 ${to ? "hover:border-[#FF6A00] transition-colors" : ""}`} data-testid={testId}>
      <div className={`text-[9px] tracking-[0.24em] uppercase font-bold ${toneMap[tone]}`}>{label}</div>
      <div className="mt-1 font-black text-lg break-words">{value ?? "—"}</div>
    </Card>
  );
  return to ? <Link to={to} data-testid={`${testId}-link`}>{body}</Link> : body;
}

// Phase 3I.4J — Recent Activity item click-through helper. Any activity
// row that carries a `business_id` opens the canonical HQ Client Contact
// page for that business. This replaces the old passive timeline with a
// drillable one without introducing a competing profile route.
function useDrillableActivity(items) {
  return React.useMemo(() => (items || []).map((it) => {
    const bid = it.metadata?.business_id || it.business_id;
    return { ...it, _drill_to: bid ? `/hq/businesses/${bid}` : null };
  }), [items]);
}

export default function HQExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  const load = React.useCallback(() => {
    let alive = true;
    hqApi.get("/control/overview").then((r) => alive && setData(r.data))
      .catch(() => alive && setErr("Something went wrong loading the dashboard."));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const cleanup = load();
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cleanup?.(); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);

  const drillActivity = useDrillableActivity(data?.recent_activity);
  if (err) return <Card className="p-6">{err}</Card>;
  const k = data?.kpis;
  const rev = data?.series?.revenue || [];
  const clients = data?.series?.clients_created || [];
  const spark = data?.series?.lead_sparkline || [];
  const revRows = rev.map((s) => ({ month: s.month, revenue: Object.values(s.by_currency || {}).reduce((a, b) => a + b, 0) / 100 }));
  const clientRows = clients.map((s) => ({ month: s.month, count: s.count }));

  const donut = Object.entries(data?.service_mix || {})
    .map(([skey, v]) => ({
      skey,
      name: SERVICE_LABEL[skey] || v?.label || skey,
      value: Number(v?.count || 0),
    }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);
  const DONUT_COLORS = ["#FF6A00", "#0A0A0A", "#8A6D00", "#4A4A4A", "#0FA958", "#C1350B"];

  // Phase 3I.4J — Campaign performance row now prioritises Interested.
  const c_sent = k?.campaigns_sent || 0;
  const c_click = k?.campaign_clicks || 0;
  const c_interested = k?.campaign_interested || 0;
  const clickRate = c_sent ? +(c_click / c_sent * 100).toFixed(1) : 0;
  const interestedRate = c_sent ? +(c_interested / c_sent * 100).toFixed(1) : 0;

  const trendPct = k?.new_leads_trend_delta_pct ?? 0;
  const trendUp = trendPct >= 0;

  return (
    <div className="space-y-6" data-testid="hq-executive-dashboard">
      {/* Row 1 — primary KPIs (drillable) */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PrimaryKPI label="Total clients" value={k?.total_clients ?? 0}
                    icon={Building2} testId="kpi-total-clients"
                    to="/hq/businesses"/>
        <PrimaryKPI label="Active clients" value={k?.active_clients_accepted ?? k?.active_clients ?? 0}
                    icon={Users} testId="kpi-active-clients"
                    to="/hq/businesses?filter_key=active"/>
        <PrimaryKPI label="New leads (unhandled)" value={k?.new_leads_unhandled ?? 0}
                    icon={trendUp ? TrendingUp : TrendingDown} testId="kpi-new-leads"
                    to="/hq/leads?stage=new">
          {/* Integrated 30-day trend + tiny sparkline replaces the old
              stand-alone Lead Growth panel. */}
          <div className="mt-3 flex items-center gap-3">
            <span className={`text-[11px] font-black tracking-[0.14em] uppercase ${trendUp ? "text-[#0FA958]" : "text-[#C1350B]"}`}
                  data-testid="kpi-new-leads-trend">
              {trendUp ? "+" : ""}{trendPct}% vs prev 30d
            </span>
            <span className="text-[10px] text-[#8A8A8A]">{k?.new_leads_30d ?? 0} last 30d</span>
          </div>
          <div className="mt-2 h-10" data-testid="kpi-new-leads-sparkline">
            {spark.length ? (
              <ResponsiveContainer width="100%" height={40}>
                <LineChart data={spark}>
                  <Line type="monotone" dataKey="count" stroke="#FF6A00" strokeWidth={2} dot={false}/>
                </LineChart>
              </ResponsiveContainer>
            ) : null}
          </div>
        </PrimaryKPI>
      </section>

      {/* Row 2 — compact operational (drillable) */}
      <section className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <MiniKPI label="Optimise users" value={k?.optimise_users ?? "—"} tone="orange" testId="kpi-optimise-users" to="/hq/businesses?service_key=orange_optimise"/>
        <MiniKPI label="OrangeCare users" value={k?.orangecare_users ?? "—"} tone="orange" testId="kpi-care-users" to="/hq/businesses?service_key=orange_care"/>
        <MiniKPI label="Revenue this month" value={totalByCur(k?.revenue_this_month_by_currency)} tone="green" testId="kpi-revenue-month" to="/hq/billing?tab=payments&period=month"/>
        <MiniKPI label="Upcoming payments" value={k?.upcoming_payments ?? "—"} testId="kpi-upcoming-payments" to="/hq/billing?tab=upcoming"/>
        <MiniKPI label="Upcoming renewals" value={k?.upcoming_renewals ?? "—"} testId="kpi-upcoming-renewals" to="/hq/billing?tab=renewals"/>
        <MiniKPI label="Outstanding invoices" value={totalByCur(k?.outstanding_invoices_by_currency)} testId="kpi-outstanding" to="/hq/billing?tab=invoices&status=outstanding"/>
        <MiniKPI label="Overdue invoices" value={totalByCur(k?.overdue_invoices_by_currency)} tone="red" testId="kpi-overdue" to="/hq/billing?tab=invoices&status=overdue"/>
      </section>

      {/* Analytics grid — Lead Growth panel removed per Phase 3I.4J. */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <Card className="p-6 xl:col-span-2" data-testid="chart-revenue">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Revenue over time</div>
          <h2 className="mt-1 font-black text-lg">Last 12 months</h2>
          <div className="mt-4">
            {!data ? <Skeleton h={260}/> : revRows.every((r) => !r.revenue) ? <EmptyState title="No revenue yet"/> : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={revRows}>
                  <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF6A00" stopOpacity={0.4}/><stop offset="100%" stopColor="#FF6A00" stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAEAEA"/>
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="#8A8A8A"/>
                  <YAxis tick={{ fontSize: 10 }} stroke="#8A8A8A"/>
                  <Tooltip formatter={(v) => v.toLocaleString()}/>
                  <Area type="monotone" dataKey="revenue" stroke="#FF6A00" strokeWidth={2.5} fill="url(#rev)" isAnimationActive animationDuration={800}/>
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Product mix — drillable donut segments. */}
        <Card className="p-6" data-testid="chart-service-mix">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Product mix</div>
          <h2 className="mt-1 font-black text-lg">Active service instances</h2>
          <div className="mt-4">
            {!data ? <Skeleton h={220}/> : donut.every((d) => !d.value) ? <EmptyState title="No active subscribers"/> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={donut} innerRadius={55} outerRadius={85} dataKey="value" isAnimationActive animationDuration={800}
                       onClick={(seg) => { if (seg?.skey) navigate(`/hq/businesses?service_key=${seg.skey}`); }}
                       style={{ cursor: "pointer" }}>
                    {donut.map((d, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} data-testid={`donut-cell-${d.skey}`}/>)}
                  </Pie>
                  <Tooltip formatter={(v, n, entry) => [`${v} · ${((v / donut.reduce((a,b)=>a+b.value,0)) * 100).toFixed(0)}%`, entry?.payload?.name]}/>
                  <Legend/>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Client growth stays — measures CRM database growth (spec §20). */}
        <Card className="p-6" data-testid="chart-clients">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Client growth</div>
          <h2 className="mt-1 font-black text-lg">New clients / month</h2>
          <div className="mt-4">
            {!data ? <Skeleton h={220}/> : clientRows.every((r) => !r.count) ? <EmptyState title="No client activity yet"/> : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={clientRows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAEAEA"/>
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="#8A8A8A"/>
                  <YAxis tick={{ fontSize: 10 }} stroke="#8A8A8A" allowDecimals={false}/>
                  <Tooltip/>
                  <Line type="monotone" dataKey="count" stroke="#0A0A0A" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive animationDuration={800}/>
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Campaign performance — Interested prioritised (spec §9-11). */}
        <Card className="p-6 xl:col-span-2" data-testid="chart-campaigns">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Campaign performance</div>
              <h2 className="mt-1 font-black text-lg">Sent · Clicks · Interested</h2>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <Link to="/hq/campaigns?filter=sent" data-testid="campaign-sent-drill" className="rounded-2xl border border-[#EAEAEA] p-4 hover:border-[#FF6A00] transition-colors">
              <div className="text-[9px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Sent</div>
              <div className="mt-1 font-black text-3xl tabular-nums">{c_sent}</div>
            </Link>
            <Link to="/hq/campaigns?filter=clicked" data-testid="campaign-clicked-drill" className="rounded-2xl border border-[#EAEAEA] p-4 hover:border-[#FF6A00] transition-colors">
              <div className="text-[9px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Clicked</div>
              <div className="mt-1 font-black text-3xl tabular-nums">{c_click}</div>
              <div className="mt-1 text-[10px] text-[#8A8A8A]">Rate {clickRate}%</div>
            </Link>
            <Link to="/hq/campaigns?filter=interested" data-testid="campaign-interested-drill" className="rounded-2xl border-2 border-[#FF6A00] bg-[#FFF7EE] p-4 hover:bg-[#FFEEDB] transition-colors">
              <div className="text-[9px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Interested</div>
              <div className="mt-1 font-black text-3xl tabular-nums text-[#0A0A0A]">{c_interested}</div>
              <div className="mt-1 text-[10px] text-[#C1350B] font-bold">Rate {interestedRate}%</div>
            </Link>
          </div>
          {data?.campaign_interested_preview?.length ? (
            <div className="mt-4 border-t border-[#EAEAEA] pt-3">
              <div className="text-[9px] tracking-[0.24em] uppercase font-bold text-[#FF6A00] mb-2">Recent interested</div>
              <ul className="space-y-1.5">
                {data.campaign_interested_preview.map((r) => (
                  <li key={r.id}>
                    <Link to={r.business_id ? `/hq/businesses/${r.business_id}` : "/hq/campaigns?filter=interested"}
                          className="flex items-center justify-between text-sm hover:text-[#FF6A00]"
                          data-testid={`interested-preview-${r.id}`}>
                      <span className="truncate">{r.business?.name || r.email || "—"}</span>
                      <span className="text-[10px] text-[#8A8A8A]">{r.updated_at?.slice(0, 10)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Card>
      </section>

      {/* Recent activity — click-throughs to client profiles per Phase 3I.4J. */}
      <Card className="p-6" data-testid="hq-recent-activity">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Recent activity</div>
            <h2 className="mt-1 font-black text-base">Eyes &amp; ears across Orange Core</h2>
          </div>
          <Link to="/hq/activity" className="text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A] hover:text-[#0A0A0A]">Full log →</Link>
        </div>
        <div className="mt-4">
          {!data ? <div className="space-y-2">{[0,1,2].map((i) => <Skeleton key={i} h={32}/>)}</div>
            : drillActivity.length ? <ActivityTimeline items={drillActivity.slice(0, 8)}/> : <EmptyState title="No activity yet" hint="New enquiries and admin actions will appear here." testId="hq-dashboard-empty"/>}
        </div>
      </Card>
    </div>
  );
}
